// app/src/lib/scale/billing.js — subscription eligibility by billing location.
//
// Kanjo is US-only for now, and sales-tax exposure varies by state. We GATE
// signups by the billing address into three outcomes:
//   - 'eligible'     → monthly OR annual billing
//   - 'annual_only'  → annual billing only (states with a 200-transaction nexus
//                      trigger that annual billing keeps us under)
//   - 'blocked'      → not offered (the owner's do-not-sell list, 2026-07-12:
//                      sales-tax-unfriendly states, plus the CITY of Chicago)
// The 4-week free trial itself lives in scale/plan.js (starts on first scan);
// this module is purely the location→eligibility decision. Sales tax is only
// collected in WA (DOR lookup at charge time).
//
// MUST stay in sync with kanjo-shared/billingStates.mjs (the server gate —
// test/subscriptionPlan.test.js enforces parity). This copy is UX only.

// Full billing (monthly + annual).
const FULL = [
  'OR', 'MT', 'NH', 'DE',            // no statewide sales tax
  'CA', 'MS', 'FL',
  'WA',                              // SaaS-taxable — we collect + remit WA sales tax
  'NY',
  'TX', 'AL',
  'IN', 'MO', 'NC', 'ND', 'WI', 'WY', 'ME', 'OK', 'ID', 'KS', 'IA',
  'PA', 'AZ', 'MA', 'NM', 'SC', 'SD', 'TN', 'UT', 'LA', 'CT', // unblocked 2026-07-12 (owner decision; under-threshold, tracked in nexus.mjs)
  'IL',                              // sellable EXCEPT the city of Chicago — see isChicago()
];
// Annual billing only (200-transaction trigger; annual billing avoids tripping it).
const ANNUAL_ONLY = ['MI', 'NJ', 'GA', 'VA', 'NV', 'NE', 'AR', 'MN'];
// Owner's do-not-sell list (2026-07-12).
const BLOCKED = ['AK', 'CO', 'DC', 'HI', 'KY', 'MD', 'OH', 'RI', 'VT', 'WV'];

/** state code → tier. Anything not listed defaults to blocked (fail closed). */
export const STATE_TIERS = (() => {
  const m = {};
  for (const s of FULL) m[s] = 'full';
  for (const s of ANNUAL_ONLY) m[s] = 'annual_only';
  for (const s of BLOCKED) m[s] = 'blocked';
  return m;
})();

/**
 * Is this address in the CITY of Chicago? City-name match is exact — "West
 * Chicago", "Chicago Heights", and "North Chicago" are different municipalities
 * and stay sellable. ZIPs 606xx are Chicago proper; 60707 and 60827 straddle
 * the city line, so they fail closed.
 * @param {{ city?:string, zip?:string }} addr
 */
export function isChicago(addr = {}) {
  const city = String(addr.city || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (city === 'chicago') return true;
  const zip = String(addr.zip || '').trim().slice(0, 5);
  return /^606\d\d$/.test(zip) || zip === '60707' || zip === '60827';
}

/**
 * Evaluate subscription eligibility for a billing location. Pure + synchronous.
 * @param {{ country?:string, state?:string, city?:string, zip?:string }} addr
 * @returns {{ status:'eligible'|'annual_only'|'blocked', billing:Array<'monthly'|'annual'>, reason:string|null }}
 */
export function evaluate(addr = {}) {
  const country = String(addr.country || 'US').toUpperCase();
  if (country !== 'US') return { status: 'blocked', billing: [], reason: 'international' };
  const state = String(addr.state || '').toUpperCase();
  if (!state) return { status: 'blocked', billing: [], reason: 'no_state' };
  if (state === 'IL') {
    // City-level gate: Chicago is blocked; the rest of IL is full. With
    // neither a city nor a ZIP we can't tell → fail closed.
    if (isChicago(addr)) return { status: 'blocked', billing: [], reason: 'chicago' };
    if (!String(addr.city || '').trim() && !String(addr.zip || '').trim()) {
      return { status: 'blocked', billing: [], reason: 'city_required' };
    }
  }
  const tier = STATE_TIERS[state];
  if (tier === 'full') return { status: 'eligible', billing: ['monthly', 'annual'], reason: null };
  if (tier === 'annual_only') return { status: 'annual_only', billing: ['annual'], reason: 'tx_trigger' };
  return { status: 'blocked', billing: [], reason: 'state_unavailable' };
}
